/**
 * Cinematic modifier taxonomy for the Experimental album-cover concept generator.
 * Lifted from the FCAA (fmr Cover Art Architect) system prompt catalog.
 *
 * The LLM picks from these when generating 3-concept album cover prompts; the UI
 * renders whichever it picked as chips beside the prompt text.
 */

export interface CinematicModifierOption {
  label: string;
  tooltip: string;
}

export interface CinematicModifierCategory {
  name: string;
  options: CinematicModifierOption[];
}

export const CINEMATIC_MODIFIERS: CinematicModifierCategory[] = [
  {
    name: '🔦 LIGHTING & ILLUMINATION',
    options: [
      { label: 'Low-Key Lighting', tooltip: 'Heavy shadows, minimal fill, moody as hell' },
      { label: 'High-Key Lighting', tooltip: 'Even, bright, low contrast (think pop, sitcom, ads)' },
      { label: 'Hard Directional Light', tooltip: 'Sharp shadows, sun-blasted, unforgiving' },
      { label: 'Soft Diffused Light', tooltip: 'Dreamy, flattering, cloud-filtered light' },
      { label: 'Practical Lighting', tooltip: 'Visible light sources in-frame (lamps, neon signs, headlights)' },
      { label: 'Back Alley Sodium Vapor Glow', tooltip: 'Sickly yellow-orange streetlight vibe' },
      { label: 'Moonlight Wash', tooltip: 'Cool blue, night exterior feel' },
      { label: 'Candle / Firelight Flicker', tooltip: 'Warm, unstable, primal' },
      { label: 'Top-Down Overhead Light', tooltip: 'Interrogation / stage spotlight energy' },
      { label: 'Underlighting', tooltip: 'Horror, menace, unnatural vibes' },
      { label: 'Golden Hour Backlighting', tooltip: 'Warm, ethereal, nostalgic light' },
      { label: 'Neon Rim Lighting', tooltip: 'Cyberpunk, futuristic, vibrant edge highlights' },
    ],
  },
  {
    name: '🌫 ATMOSPHERIC & PARTICLE EFFECTS',
    options: [
      { label: 'Smoke Haze', tooltip: 'Concert stage / bar / club realism' },
      { label: 'Dust Particles in Light Beams', tooltip: 'Sun-through-barn / abandoned building gold' },
      { label: 'Ashfall / Ember Drift', tooltip: 'Post-apocalyptic or inferno-adjacent' },
      { label: 'Mist at Ground Level', tooltip: 'Swamp, dawn, cemetery, low-lying fog' },
      { label: 'Heat Distortion / Mirage', tooltip: 'Southern heat, desert roads' },
      { label: 'Rain-in-Light Shafts', tooltip: 'Blade Runner, noir, emotional pain button' },
      { label: 'Snow Flurries', tooltip: 'Soft motion, cold stillness' },
      { label: 'Wind-Blown Debris', tooltip: 'Chaos, unrest, movement' },
    ],
  },
  {
    name: '🌅 TIME, WEATHER & SKY',
    options: [
      { label: 'Blue Hour Twilight', tooltip: 'Pre-sunrise / post-sunset cinematic sweet spot' },
      { label: 'Overcast Flat Sky', tooltip: 'Muted, oppressive, real-world grit' },
      { label: 'Storm Front Rolling In', tooltip: 'Ominous, dramatic tension' },
      { label: 'Lightning Flashes', tooltip: 'High drama, frozen intensity' },
      { label: 'Sun Through Broken Clouds', tooltip: 'Redemption / divine metaphor territory' },
      { label: 'Blood-Red Sunset', tooltip: 'Heat, danger, end-of-days vibes' },
      { label: 'Night Sky Glow / Light Pollution', tooltip: 'Urban realism' },
      { label: 'Star-Dense Rural Sky', tooltip: 'Isolation, vastness' },
    ],
  },
  {
    name: '🎥 CAMERA & OPTICAL',
    options: [
      { label: 'Anamorphic Lens Flares', tooltip: 'Cinematic widescreen DNA' },
      { label: 'Lens Bloom / Halation', tooltip: 'Vintage film stock feel' },
      { label: 'Motion Blur from Long Exposure', tooltip: 'Speed, chaos, time passing' },
      { label: 'Foreground Occlusion', tooltip: 'Shooting through objects (fences, glass, leaves)' },
      { label: 'Atmospheric Depth Falloff', tooltip: 'Distant objects fading into haze' },
      { label: 'Reflections & Wet Surfaces', tooltip: 'Rain-soaked streets, neon bounce' },
    ],
  },
  {
    name: '🧱 SURFACE & TEXTURE',
    options: [
      { label: 'Wet Asphalt Reflections', tooltip: 'Urban noir, post-rain sheen' },
      { label: 'Cracked Concrete & Peeling Paint', tooltip: 'Decay, history, urban texture' },
      { label: 'Rust & Oxidation', tooltip: 'Industrial decay, aging metal' },
      { label: 'Oil-Stained Ground', tooltip: 'Mechanic shop, gritty realism' },
      { label: 'Mold, Moss, and Rot', tooltip: 'Organic decay, damp environments' },
      { label: 'Dust-Caked Surfaces', tooltip: 'Neglect, abandonment, arid conditions' },
      { label: 'Sun-Bleached Materials', tooltip: 'Desert exposure, faded memories' },
    ],
  },
  {
    name: '🎭 MOOD & ENERGY',
    options: [
      { label: 'Oppressive / Claustrophobic Atmosphere', tooltip: 'Tight spaces, high tension' },
      { label: 'Serene / Still Air', tooltip: 'Calm, meditative, peaceful' },
      { label: 'Tense Pre-Action Silence', tooltip: 'The moment before everything kicks off' },
      { label: 'Chaotic Kinetic Energy', tooltip: 'Action, movement, high energy' },
      { label: 'Dreamlike / Surreal Calm', tooltip: 'Lynchian, ethereal, uncanny valley' },
      { label: 'Gritty Realism', tooltip: 'Documentary style, unpolished reality' },
      { label: 'Mythic / Epic Scale', tooltip: 'Vast landscapes, legendary feel' },
    ],
  },
  {
    name: '🔥 HIGH-VALUE CHEAT CODES',
    options: [
      { label: 'Volumetric Light Shafts', tooltip: 'God rays, dramatic and focused light beams' },
      { label: 'Backlit Particulate Glow', tooltip: 'Floating dust/smoke catching light from behind' },
      { label: 'Edge Highlight Separation', tooltip: 'Using light to carve a subject out from the background' },
      { label: 'Color Temperature Contrast', tooltip: 'Warm foreground vs. cool background, or vice versa' },
      { label: 'Selective Illumination', tooltip: 'Only the key subject is lit, the world fades to black' },
    ],
  },
];

/** Category name → tooltip lookup, for chip hovercards. */
export const MODIFIER_TOOLTIPS: Record<string, string> = CINEMATIC_MODIFIERS
  .flatMap(c => c.options)
  .reduce<Record<string, string>>((acc, o) => {
    acc[o.label] = o.tooltip;
    return acc;
  }, {});
